import * as React from 'react';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { FastField, Field, Formik, useField, useFormikContext } from 'formik';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-duotone-svg-icons';

import { ReactComponent as AddOn } from '../shared/icons/AddOn.svg';
import { ReactComponent as StandAlone } from '../shared/icons/StandAlone.svg';
import { ReactComponent as PartnerBuyout } from '../shared/icons/PartnerBuyout.svg';
import { ReactComponent as ManagementBuyout } from '../shared/icons/ManagementBuyout.svg';

import {
	Box,
	Grid,
	Skeleton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import {
	CerebroBreadcrumb,
	CerebroDrawer,
	CerebroHeading,
	CerebroIcon,
	CerebroLabel,
	CerebroPageContainer,
	CerebroPrimaryButton,
	CerebroText,
	IconRadioGroup,
	IconRadioButton,
	CerebroMaskedInput,
	CerebroYesNo,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroInput,
	CerebroForm,
} from '../shared/components';

import {
	AcquisitionEvaluationStage,
	AcquisitionEvaluationStageMap,
	AcquisitionTransactionType,
	AcquisitionTransactionTypeMap,
	DealRequestAllocation,
	DealType,
	NatureOfBuyerType,
	NatureOfBuyerTypeMap,
} from '../shared/models';
import { buildNavItems, useApi, useAuth, useCerebroNav } from '../shared/context';
import { QueryKeys, useDealQuery, useDealRequestAllocationQuery } from '../shared/hooks';
import { NumberUtils } from '../shared/utils';
import { CerebroApiEndpoints } from '../shared/utils/api';
import { MaxCurrencyLength } from '../shared/utils/platform';
import { getApiResponseData } from '../shared/utils/axios';

const AcquisitionTotalsField = (props: { name: string; equityRoll: string }) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));

	const { values, touched, setFieldValue } = useFormikContext<DealRequestAllocationAcquisitionValues>();

	const valueOfferedAmount = NumberUtils.ParseDecimal(values.valueOfferedAmount, 0);
	const hasEarnout = values.hasEarnout ?? false;
	const earnoutAmount = NumberUtils.ParseDecimal(values.earnoutAmount, 0);
	const workingCapitalLineAmount = NumberUtils.ParseDecimal(values.workingCapitalForAcquisitionAmount, 0);
	const hasWorkingCapitalLine = (values.workingCapitalForAcquisition ?? false) == true;
	const percentagePurchased = NumberUtils.ParseDecimal(values.percentageBeingPurchased, 0);
	const sellerEquityRollAmount = NumberUtils.ParseDecimal(values.sellerEquityRollAmount, 0);
	const provideSellerFinancing = values.provideSellerFinancing ?? false;
	const sellerFinancingAmount = NumberUtils.ParseDecimal(values.sellerFinancingAmount, 0);
	const buyerCashAmount = NumberUtils.ParseDecimal(values.buyerCashAmount, 0);
	const buyerProvidingCash = values.buyerProvidingCash ?? false;
	const totalAcquisitionAmount = NumberUtils.FormatNumber(values.totalAcquisitionAmount, 'currency', {
		maximumFractionDigits: 0,
	});
	const acquisitionTransactionCostsAmount = NumberUtils.ParseDecimal(values.acquisitionTransactionCostsAmount, 0);
	const acquisitionWorkingCapitalContributionAmount = NumberUtils.ParseDecimal(
		values.acquisitionWorkingCapitalContributionAmount,
		0
	);

	const [field, meta] = useField(props);

	React.useEffect(() => {
		setFieldValue(
			props.name,
			calculateAcquisitionTotalAmount(
				valueOfferedAmount,
				hasEarnout,
				earnoutAmount,
				sellerEquityRollAmount,
				provideSellerFinancing ?? false,
				sellerFinancingAmount,
				buyerProvidingCash ?? false,
				buyerCashAmount,
				hasWorkingCapitalLine,
				workingCapitalLineAmount,
				acquisitionTransactionCostsAmount,
				acquisitionWorkingCapitalContributionAmount
			)
		);
	}, [
		valueOfferedAmount,
		percentagePurchased,
		sellerEquityRollAmount,
		provideSellerFinancing,
		sellerFinancingAmount,
		buyerProvidingCash,
		buyerCashAmount,
		hasEarnout,
		earnoutAmount,
		touched.valueOfferedAmount,
		setFieldValue,
		props.name,
		hasWorkingCapitalLine,
		workingCapitalLineAmount,
		acquisitionTransactionCostsAmount,
		acquisitionWorkingCapitalContributionAmount,
	]);

	const formattedOfferedAmount = NumberUtils.FormatNumber(valueOfferedAmount, 'currency', {
		maximumFractionDigits: 0,
	});

	const formattedCashProvided = NumberUtils.FormatNumber(buyerCashAmount, 'currency', {
		maximumFractionDigits: 0,
	});

	const formattedTransactionCosts = NumberUtils.FormatNumber(acquisitionTransactionCostsAmount, 'currency', {
		maximumFractionDigits: 0,
	});

	const formattedWorkingCapitalContribution = NumberUtils.FormatNumber(
		acquisitionWorkingCapitalContributionAmount,
		'currency',
		{
			maximumFractionDigits: 0,
		}
	);

	const formattedEquityRollProvided = NumberUtils.FormatNumber(sellerEquityRollAmount, 'currency', {
		maximumFractionDigits: 0,
	});

	const formattedSellerProvided = NumberUtils.FormatNumber(sellerFinancingAmount, 'currency', {
		maximumFractionDigits: 0,
	});

	const formattedEarnoutAmount = NumberUtils.FormatNumber(earnoutAmount, 'currency', {
		maximumFractionDigits: 0,
	});

	const formattedWorkingCapitalLineAmount = NumberUtils.FormatNumber(workingCapitalLineAmount, 'currency', {
		maximumFractionDigits: 0,
	});

	let cashProvidedFormula = `${formattedCashProvided} (Cash)`;
	if (acquisitionTransactionCostsAmount > 0) {
		cashProvidedFormula += ` + ${formattedTransactionCosts} (Transaction Costs)`;
	}
	if (acquisitionWorkingCapitalContributionAmount > 0) {
		cashProvidedFormula += ` + ${formattedWorkingCapitalContribution} (Working Capital)`;
	}

	const formula = hasWorkingCapitalLine
		? `${formattedOfferedAmount} (Offered Value) - ${formattedEquityRollProvided} (Equity Roll) - ${formattedEarnoutAmount} (Earnout) - ${cashProvidedFormula} - ${formattedWorkingCapitalLineAmount} (From Working Capital Line) - ${formattedSellerProvided} (Seller Financing)`
		: `${formattedOfferedAmount} (Offered Value) - ${formattedEquityRollProvided} (Equity Roll) - ${formattedEarnoutAmount} (Earnout) - ${cashProvidedFormula} - ${formattedSellerProvided} (Seller Financing)`;

	if (isMobile) {
		return (
			<>
				<CerebroHeading size="h2" component="h2" sx={{ marginY: 2, textAlign: 'center' }}>
					Acquisition Totals
				</CerebroHeading>
				<CerebroText variant="hint" align="center" gutterBottom>
					{formula}
				</CerebroText>
				<CerebroHeading
					size="h3"
					component="h3"
					align="center"
					gutterBottom
					sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 3 }}
				>
					Total Requested Amount =
					<CerebroText component="span" variant="large" sx={{ paddingLeft: 1 }}>
						{totalAcquisitionAmount}
					</CerebroText>
				</CerebroHeading>
			</>
		);
	}

	return (
		<>
			<Grid container spacing={2} alignItems="stretch">
				<TableContainer sx={{ marginTop: 3 }}>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell colSpan={3}>Acquisition Totals</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							<TableRow>
								<TableCell width={400}>Total Business Value</TableCell>
								<TableCell width={50}></TableCell>
								<TableCell>{formattedOfferedAmount}</TableCell>
							</TableRow>
							<TableRow>
								<TableCell width={400}>Equity Roll</TableCell>
								<TableCell width={50}>-</TableCell>
								<TableCell>{formattedEquityRollProvided}</TableCell>
							</TableRow>
							<TableRow>
								<TableCell width={400}>Earnout</TableCell>
								<TableCell width={50}>-</TableCell>
								<TableCell>{formattedEarnoutAmount}</TableCell>
							</TableRow>
							<TableRow>
								<TableCell width={400}>Cash Provided</TableCell>
								<TableCell width={50}>-</TableCell>
								<TableCell>{formattedCashProvided}</TableCell>
							</TableRow>
							{acquisitionTransactionCostsAmount > 0 && (
								<TableRow>
									<TableCell width={400}>Transaction Costs</TableCell>
									<TableCell width={50}>+</TableCell>
									<TableCell>{formattedTransactionCosts}</TableCell>
								</TableRow>
							)}
							{acquisitionWorkingCapitalContributionAmount > 0 && (
								<TableRow>
									<TableCell width={400}>Working Capital</TableCell>
									<TableCell width={50}>+</TableCell>
									<TableCell>{formattedWorkingCapitalContribution}</TableCell>
								</TableRow>
							)}
							{hasWorkingCapitalLine && (
								<TableRow>
									<TableCell width={400}>From Working Capital Line</TableCell>
									<TableCell width={50}>-</TableCell>
									<TableCell>{formattedWorkingCapitalLineAmount}</TableCell>
								</TableRow>
							)}
							<TableRow>
								<TableCell width={400}>Seller Amount Provided</TableCell>
								<TableCell width={50}>-</TableCell>
								<TableCell>{formattedSellerProvided}</TableCell>
							</TableRow>
							<TableRow>
								<TableCell width={400}>Total Requested</TableCell>
								<TableCell width={50}>=</TableCell>
								<TableCell>{totalAcquisitionAmount}</TableCell>
							</TableRow>
						</TableBody>
					</Table>
				</TableContainer>
			</Grid>
			{!!meta.touched && !!meta.error && <div>{meta.error}</div>}
		</>
	);
};

export const BusinessAcquisition: React.FC = () => {
	const [open, setOpen] = React.useState(false);
	const api = useApi();
	const { currentUser } = useAuth();
	const snackbar = useSnackbar();
	const navigate = useNavigate();
	const nav = useCerebroNav();

	const client = useQueryClient();

	const { guid } = useParams<'guid'>();
	const dealQuery = useDealQuery(guid);
	const allocationQuery = useDealRequestAllocationQuery(guid);

	const { mutate } = useMutation(
		async (values: DealRequestAllocationAcquisitionValues) => {
			const isAcquisition =
				values.dealType === 'AcquisitionManagementBuyout' || values.dealType === 'AcquisitionPartnerBuyout';
			const payload: Partial<DealRequestAllocation> = {
				hasMachineryEquipment: !isAcquisition ? allocationQuery.data?.purchaseEquipment : false,
				hasOwnerOccupiedRealEstate: !isAcquisition ? allocationQuery.data?.purchaseRealEstate : false,
				hasCashMarketables: !isAcquisition
					? allocationQuery.data?.financeExpensesOrGrowth || allocationQuery.data?.purchaseWorkingCapital
					: false,
				hasAccountsReceivables: !isAcquisition
					? allocationQuery.data?.financeExpensesOrGrowth || allocationQuery.data?.purchaseWorkingCapital
					: false,
				hasInventory: !isAcquisition
					? allocationQuery.data?.financeExpensesOrGrowth || allocationQuery.data?.purchaseWorkingCapital
					: false,

				acquisitionHasMachineryEquipment: isAcquisition ? allocationQuery.data?.purchaseEquipment : false,
				acquisitionHasOwnerOccupiedRealEstate: isAcquisition ? allocationQuery.data?.purchaseRealEstate : false,
				acquisitionHasCashMarketables: isAcquisition
					? allocationQuery.data?.financeExpensesOrGrowth || allocationQuery.data?.purchaseWorkingCapital
					: false,
				acquisitionHasAccountsReceivables: isAcquisition
					? allocationQuery.data?.financeExpensesOrGrowth || allocationQuery.data?.purchaseWorkingCapital
					: false,
				acquisitionHasInventory: isAcquisition
					? allocationQuery.data?.financeExpensesOrGrowth || allocationQuery.data?.purchaseWorkingCapital
					: false,

				dealType: values.dealType,
				percentageBeingPurchased: NumberUtils.ParseDecimal(values.percentageBeingPurchased),
				acquisitionEvaluationStage: values.acquisitionEvaluationStage,
				natureOfBuyer: values.natureOfBuyer,
				transactionType: values.transactionType,
				acquisitionYearsInOperation: NumberUtils.ParseDecimal(values.acquisitionYearsInOperation),
				hasLegalCouncel: values.hasLegalCouncel,
				legalFirmName: values.legalFirmName ?? '',
				hasCurrentOwnership: values.hasCurrentOwnership,
				currentOwnershipPercentage: NumberUtils.ParseDecimal(values.currentOwnershipPercentage),
				valueOfferedAmount: NumberUtils.ParseDecimal(values.valueOfferedAmount),
				sellerEquityRollAmount: NumberUtils.ParseDecimal(values.sellerEquityRollAmount),
				provideSellerFinancing: values.provideSellerFinancing,
				sellerFinancingAmount:
					values.provideSellerFinancing === true ? NumberUtils.ParseDecimal(values.sellerFinancingAmount) : 0,
				buyerProvidingCash: values.buyerProvidingCash,
				buyerCashAmount:
					values.buyerProvidingCash === true ? NumberUtils.ParseDecimal(values.buyerCashAmount) : 0,
				acquisitionTransactionCostsAmount:
					values.buyerProvidingCash === true
						? NumberUtils.ParseDecimal(values.acquisitionTransactionCostsAmount)
						: 0,
				acquisitionWorkingCapitalContributionAmount:
					values.buyerProvidingCash === true
						? NumberUtils.ParseDecimal(values.acquisitionWorkingCapitalContributionAmount)
						: 0,
				hasEarnout: values.hasEarnout,
				earnoutAmount: values.hasEarnout === true ? NumberUtils.ParseDecimal(values.earnoutAmount) : 0,
			};

			const { data, error } = await getApiResponseData<DealRequestAllocation>(
				api.patch(CerebroApiEndpoints.Allocation(guid), payload)
			);

			if (error) {
				throw new Error(error.response?.data);
			}
			if (data == null) {
				throw new Error('Could not save allocation data.');
			}

			return data;
		},
		{
			onSuccess: allocation => {
				client.setQueryData(QueryKeys.Allocation(guid), allocation);

				const navState = buildNavItems(
					currentUser,
					allocation,
					dealQuery.data,
					location.pathname,
					client.getQueryData(QueryKeys.Questions.LoanApp),
					client.getQueryData(QueryKeys.Product(guid)),
					client.getQueryData(QueryKeys.RequiredDocuments.Root(guid)),
					client.getQueryData(QueryKeys.Integrations.Config())
				);
				nav.setState(navState);

				client.invalidateQueries(QueryKeys.Deal.Root(guid));
				client.invalidateQueries(QueryKeys.Matching.Get(guid));

				snackbar.enqueueSnackbar('Progress Saved.', { variant: 'success' });
				if (navState.nextNavItem !== undefined) {
					navigate(`/${guid}/${navState.nextNavItem.url}`);
				}
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, { variant: 'error' });
			},
		}
	);

	if (allocationQuery.isLoading) {
		return <Skeleton height={400} />;
	}

	return (
		<CerebroPageContainer maxWidth={'80%'}>
			<CerebroBreadcrumb>Deal &gt; Acquisition Details</CerebroBreadcrumb>

			<CerebroHeading sx={{ marginBottom: 3 }}>Tell us about your business acquisition</CerebroHeading>

			<CerebroText sx={{ marginBottom: 3 }}>
				Details on your business acquisition will allow better lender matching and loan options.
			</CerebroText>

			<Formik
				initialValues={{
					dealType: allocationQuery.data?.dealType,
					percentageBeingPurchased: NumberUtils.FormatPercentage(
						allocationQuery.data?.percentageBeingPurchased ?? 100
					),
					acquisitionEvaluationStage: allocationQuery.data?.acquisitionEvaluationStage,
					natureOfBuyer: allocationQuery.data?.natureOfBuyer,
					acquisitionYearsInOperation: NumberUtils.FormatDecimal(
						allocationQuery.data?.acquisitionYearsInOperation
					),
					transactionType: allocationQuery.data?.transactionType,
					hasLegalCouncel: allocationQuery.data?.hasLegalCouncel,
					legalFirmName: allocationQuery.data?.legalFirmName ?? '',
					hasCurrentOwnership: allocationQuery.data?.hasCurrentOwnership,
					currentOwnershipPercentage: NumberUtils.FormatPercentage(
						allocationQuery.data?.currentOwnershipPercentage
					),
					valueOfferedAmount: NumberUtils.FormatCurrency(allocationQuery.data?.valueOfferedAmount),
					sellerEquityRollAmount: NumberUtils.FormatCurrency(allocationQuery.data?.sellerEquityRollAmount),
					provideSellerFinancing: allocationQuery.data?.provideSellerFinancing,
					sellerFinancingAmount: NumberUtils.FormatCurrency(allocationQuery.data?.sellerFinancingAmount),
					buyerProvidingCash: allocationQuery.data?.buyerProvidingCash,
					buyerCashAmount: NumberUtils.FormatCurrency(allocationQuery.data?.buyerCashAmount),
					hasEarnout: allocationQuery.data?.hasEarnout,
					acquisitionTransactionCostsAmount: NumberUtils.FormatCurrency(
						allocationQuery.data?.acquisitionTransactionCostsAmount
					),
					acquisitionWorkingCapitalContributionAmount: NumberUtils.FormatCurrency(
						allocationQuery.data?.acquisitionWorkingCapitalContributionAmount
					),
					earnoutAmount: NumberUtils.FormatCurrency(allocationQuery.data?.earnoutAmount),
					totalAcquisitionAmount: calculateAcquisitionTotalAmount(
						allocationQuery.data?.valueOfferedAmount ?? 0,
						allocationQuery.data?.hasEarnout ?? false,
						allocationQuery.data?.earnoutAmount ?? 0,
						allocationQuery.data?.sellerEquityRollAmount ?? 0,
						allocationQuery.data?.provideSellerFinancing ?? false,
						allocationQuery.data?.sellerFinancingAmount ?? 0,
						allocationQuery.data?.buyerProvidingCash ?? false,
						allocationQuery.data?.buyerCashAmount ?? 0,
						allocationQuery.data?.workingCapitalForAcquisition ?? false,
						allocationQuery.data?.workingCapitalForAcquisitionAmount ?? 0,
						allocationQuery.data?.acquisitionTransactionCostsAmount ?? 0,
						allocationQuery.data?.acquisitionWorkingCapitalContributionAmount ?? 0
					),
					workingCapitalForAcquisition: allocationQuery.data?.workingCapitalForAcquisition ?? false,
					workingCapitalForAcquisitionAmount: NumberUtils.FormatCurrency(
						allocationQuery.data?.workingCapitalForAcquisitionAmount
					),
				}}
				validate={validateForm}
				onSubmit={values => mutate(values)}
			>
				{({ isSubmitting, errors, touched, handleSubmit, values, setFieldValue }) => (
					<CerebroForm onSubmit={handleSubmit}>
						<Box sx={{ marginBottom: 2 }}>
							<CerebroLabel
								required
								icon={() => <FontAwesomeIcon icon={faInfoCircle} />}
								onIconClick={() => setOpen(true)}
							>
								What type of business acquisition are you doing?
							</CerebroLabel>
							<Field
								sx={{ marginY: 2 }}
								as={IconRadioGroup}
								name="dealType"
								disabled={isSubmitting}
								error={touched.dealType && errors.dealType}
							>
								<Grid container spacing={2} sx={{ marginBottom: 4 }}>
									<Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
										<IconRadioButton stretch value="Acquisitions" icon={() => <StandAlone />}>
											Stand-Alone
										</IconRadioButton>
									</Grid>
									<Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
										<IconRadioButton stretch value="AcquisitionAddon" icon={() => <AddOn />}>
											Add-On
										</IconRadioButton>
									</Grid>
									<Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
										<IconRadioButton
											stretch
											value="AcquisitionPartnerBuyout"
											icon={() => <PartnerBuyout />}
										>
											Partner Buy-Out
										</IconRadioButton>
									</Grid>
									<Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
										<IconRadioButton
											stretch
											value="AcquisitionManagementBuyout"
											icon={() => <ManagementBuyout />}
										>
											Management Buy-Out
										</IconRadioButton>
									</Grid>
								</Grid>
							</Field>
							{touched.dealType && errors.dealType && (
								<CerebroText variant="error" sx={{ marginBottom: 2 }}>
									{errors.dealType}
								</CerebroText>
							)}
						</Box>

						<hr style={{ marginTop: 7 }} />

						<Grid container spacing={2} sx={{ marginTop: 3 }}>
							<Grid item xs={12} sm={12} md={6}>
								<Box sx={{ marginBottom: 2 }}>
									<CerebroLabel
										htmlFor="acquisitionEvaluationStage"
										error={
											errors.acquisitionEvaluationStage !== undefined &&
											touched.acquisitionEvaluationStage
										}
									>
										What stage are you in evaluating the acquisition target?
									</CerebroLabel>

									<Field
										as={CerebroDropdown}
										id="acquisitionEvaluationStage"
										name="acquisitionEvaluationStage"
										disabled={isSubmitting}
										value={values.acquisitionEvaluationStage}
										error={
											errors.acquisitionEvaluationStage !== undefined &&
											touched.acquisitionEvaluationStage
										}
									>
										{Object.entries(AcquisitionEvaluationStageMap).map(([value, description]) => (
											<CerebroDropdownMenuItem key={value} value={value}>
												{description}
											</CerebroDropdownMenuItem>
										))}
									</Field>

									{touched.acquisitionEvaluationStage &&
										errors.acquisitionEvaluationStage != null && (
											<CerebroText variant="error">
												{errors.acquisitionEvaluationStage}
											</CerebroText>
										)}
								</Box>
							</Grid>
						</Grid>

						<Grid container spacing={2}>
							<Grid item xs={12} sm={12} md={6}>
								<Box sx={{ marginBottom: 2, marginTop: 3 }}>
									<CerebroLabel
										required
										htmlFor="acquisitionYearsInOperation"
										error={
											errors.acquisitionYearsInOperation !== undefined &&
											touched.acquisitionYearsInOperation
										}
									>
										How many years has the acquisition target been operating?
									</CerebroLabel>

									<Field
										as={CerebroMaskedInput}
										id="acquisitionYearsInOperation"
										name="acquisitionYearsInOperation"
										disabled={isSubmitting}
										value={values.acquisitionYearsInOperation}
										error={
											touched.acquisitionYearsInOperation && errors.acquisitionYearsInOperation
										}
									/>
								</Box>
							</Grid>
						</Grid>
						{touched.acquisitionYearsInOperation && errors.acquisitionYearsInOperation != null && (
							<CerebroText variant="error">{errors.acquisitionYearsInOperation}</CerebroText>
						)}

						<Grid container spacing={2}>
							<Grid item xs={12} sm={12} md={6}>
								<Box sx={{ marginBottom: 2, marginTop: 3 }}>
									<CerebroLabel
										htmlFor="transactionType"
										error={errors.transactionType !== undefined && touched.transactionType}
									>
										What is the transaction type?
									</CerebroLabel>

									<Field
										as={CerebroDropdown}
										id="transactionType"
										name="transactionType"
										disabled={isSubmitting}
										value={values.transactionType}
										error={errors.transactionType !== undefined && touched.transactionType}
									>
										{Object.entries(AcquisitionTransactionTypeMap).map(([value, description]) => (
											<CerebroDropdownMenuItem key={value} value={value}>
												{description}
											</CerebroDropdownMenuItem>
										))}
									</Field>

									{touched.transactionType && errors.transactionType != null && (
										<CerebroText variant="error">{errors.transactionType}</CerebroText>
									)}
								</Box>
							</Grid>
							<Grid item xs={12} sm={12} md={6}>
								<Box sx={{ marginBottom: 2, marginTop: 3 }}>
									<CerebroLabel
										htmlFor="natureOfBuyer"
										error={errors.natureOfBuyer !== undefined && touched.natureOfBuyer}
									>
										What is the nature of the buyer?
									</CerebroLabel>

									<Field
										as={CerebroDropdown}
										id="natureOfBuyer"
										name="natureOfBuyer"
										disabled={isSubmitting}
										value={values.natureOfBuyer}
										error={errors.natureOfBuyer !== undefined && touched.natureOfBuyer}
									>
										{Object.entries(NatureOfBuyerTypeMap).map(([value, description]) => (
											<CerebroDropdownMenuItem key={value} value={value}>
												{description}
											</CerebroDropdownMenuItem>
										))}
									</Field>

									{touched.natureOfBuyer && errors.natureOfBuyer != null && (
										<CerebroText variant="error">{errors.natureOfBuyer}</CerebroText>
									)}
								</Box>
							</Grid>
						</Grid>

						<hr style={{ marginTop: 7 }} />

						<Grid container spacing={2} sx={{ marginTop: 3 }}>
							<Grid item xs={12}>
								<CerebroLabel
									required
									htmlFor="valueOfferedAmount"
									sx={{ marginTop: 3, marginBottom: 1 }}
								>
									What is the total value of the business being acquired?
								</CerebroLabel>
								<CerebroText variant="hint">
									Enter the value for 100% of the business, even if less than the full business is
									being acquired.
								</CerebroText>
							</Grid>
							<Grid item xs={12} sm={12} md={6}>
								<Box sx={{ marginBottom: 2 }}>
									<Field
										formatas="currency"
										as={CerebroMaskedInput}
										id="valueOfferedAmount"
										name="valueOfferedAmount"
										maxLength={MaxCurrencyLength}
										disabled={isSubmitting}
										value={values.valueOfferedAmount}
										error={touched.valueOfferedAmount && errors.valueOfferedAmount}
										onBlur={() => {
											const valueOfferedAmount = NumberUtils.ParseDecimal(
												values.valueOfferedAmount,
												0
											);
											const percentagePurchased = NumberUtils.ParseDecimal(
												values.percentageBeingPurchased,
												0
											);
											const sellerEquityRollAmount = NumberUtils.ParseDecimal(
												values.sellerEquityRollAmount,
												0
											);
											runEquityAmountCalculation(
												valueOfferedAmount,
												percentagePurchased,
												sellerEquityRollAmount,
												setFieldValue
											);
										}}
									/>
								</Box>
							</Grid>
						</Grid>
						{touched.valueOfferedAmount && errors.valueOfferedAmount && (
							<CerebroText variant="error" sx={{ marginBottom: 2 }}>
								{errors.valueOfferedAmount}
							</CerebroText>
						)}

						<Grid container spacing={2}>
							<Grid item xs={12} sx={{ marginTop: 3 }}>
								<CerebroLabel required htmlFor="percentageBeingPurchased">
									What percentage of the business is being purchased from the seller?
								</CerebroLabel>
							</Grid>
							<Grid item xs={12} sm={12} md={6}>
								<Box sx={{ marginBottom: 2 }}>
									<Field
										formatas="percent"
										as={CerebroMaskedInput}
										id="percentageBeingPurchased"
										name="percentageBeingPurchased"
										disabled={isSubmitting}
										value={values.percentageBeingPurchased}
										error={touched.percentageBeingPurchased && errors.percentageBeingPurchased}
										onBlur={() => {
											const valueOfferedAmount = NumberUtils.ParseDecimal(
												values.valueOfferedAmount,
												0
											);
											const percentagePurchased = NumberUtils.ParseDecimal(
												values.percentageBeingPurchased,
												0
											);
											const sellerEquityRollAmount = NumberUtils.ParseDecimal(
												values.sellerEquityRollAmount,
												0
											);
											runEquityAmountCalculation(
												valueOfferedAmount,
												percentagePurchased,
												sellerEquityRollAmount,
												setFieldValue
											);
										}}
									/>
								</Box>
							</Grid>
						</Grid>
						{touched.percentageBeingPurchased && errors.percentageBeingPurchased && (
							<CerebroText variant="error" sx={{ marginBottom: 2 }}>
								{errors.percentageBeingPurchased}
							</CerebroText>
						)}

						{values.percentageBeingPurchased && values.percentageBeingPurchased != '100%' && (
							<>
								<Grid container spacing={2} sx={{ marginTop: 3 }}>
									<Grid item xs={12}>
										<CerebroLabel
											required
											htmlFor="sellerEquityRollAmount"
											sx={{ marginTop: 3, marginBottom: 1 }}
										>
											What is the seller equity roll amount?
										</CerebroLabel>
									</Grid>
									<Grid item xs={12} sm={12} md={6}>
										<Box sx={{ marginBottom: 2 }}>
											<Field
												formatas="currency"
												as={CerebroMaskedInput}
												maxLength={MaxCurrencyLength}
												id="sellerEquityRollAmount"
												name="sellerEquityRollAmount"
												disabled={isSubmitting}
												value={values.sellerEquityRollAmount}
												error={touched.sellerEquityRollAmount && errors.sellerEquityRollAmount}
											/>
										</Box>
									</Grid>
								</Grid>
								{touched.sellerEquityRollAmount && errors.sellerEquityRollAmount && (
									<CerebroText variant="error" sx={{ marginBottom: 2 }}>
										{errors.sellerEquityRollAmount}
									</CerebroText>
								)}
							</>
						)}

						<Grid container spacing={2}>
							<Grid item xs={12}>
								<CerebroLabel sx={{ marginTop: 3 }} htmlFor="provideSellerFinancing">
									Is the seller willing to provide seller financing?
								</CerebroLabel>
							</Grid>
							<Grid item xs={12}>
								<Box sx={{ marginBottom: 2 }}>
									<Field as={CerebroYesNo} name="provideSellerFinancing" disabled={isSubmitting} />
								</Box>
							</Grid>
						</Grid>

						{values.provideSellerFinancing === true ? (
							<Grid container spacing={2}>
								<Grid item xs={12}>
									<CerebroLabel required htmlFor="sellerFinancingAmount">
										What is the seller financing amount?
									</CerebroLabel>
								</Grid>
								<Grid item xs={12} sm={12} md={6}>
									<Box sx={{ marginBottom: 2 }}>
										<Field
											formatas="currency"
											as={CerebroMaskedInput}
											maxLength={MaxCurrencyLength}
											id="sellerFinancingAmount"
											name="sellerFinancingAmount"
											disabled={isSubmitting}
											value={values.sellerFinancingAmount}
											error={touched.sellerFinancingAmount && errors.sellerFinancingAmount}
										/>
									</Box>
								</Grid>
							</Grid>
						) : (
							<></>
						)}
						{touched.sellerFinancingAmount && errors.sellerFinancingAmount && (
							<CerebroText variant="error" sx={{ marginBottom: 2 }}>
								{errors.sellerFinancingAmount}
							</CerebroText>
						)}

						<Grid container spacing={2}>
							<Grid item xs={12}>
								<CerebroLabel sx={{ marginTop: 3 }} htmlFor="hasEarnout">
									Is there a deferred earnout due to the seller?
								</CerebroLabel>
							</Grid>
							<Grid item xs={12}>
								<Box sx={{ marginBottom: 2 }}>
									<Field as={CerebroYesNo} name="hasEarnout" disabled={isSubmitting} />
								</Box>
							</Grid>
						</Grid>

						{values.hasEarnout === true ? (
							<Grid container spacing={2}>
								<Grid item xs={12}>
									<CerebroLabel required htmlFor="earnoutAmount">
										What is the deferred earnout amount?
									</CerebroLabel>
								</Grid>
								<Grid item xs={12} sm={12} md={6}>
									<Box sx={{ marginBottom: 2 }}>
										<Field
											formatas="currency"
											as={CerebroMaskedInput}
											maxLength={MaxCurrencyLength}
											id="earnoutAmount"
											name="earnoutAmount"
											disabled={isSubmitting}
											value={values.earnoutAmount}
											error={touched.earnoutAmount && errors.earnoutAmount}
										/>
									</Box>
								</Grid>
							</Grid>
						) : (
							<></>
						)}
						{touched.earnoutAmount && errors.earnoutAmount && (
							<CerebroText variant="error" sx={{ marginBottom: 2 }}>
								{errors.earnoutAmount}
							</CerebroText>
						)}

						<Grid container>
							<Grid item xs={12}>
								<CerebroLabel sx={{ marginTop: 3, marginBottom: 1 }} htmlFor="buyerProvidingCash">
									Is the buyer willing to provide cash?
								</CerebroLabel>
								<CerebroText variant="hint" gutterBottom>
									The closer to 30% cash equity, the high probability of closing a successful
									financing. Less than 5% cash equity is unlikely to win lender interest.
								</CerebroText>
							</Grid>
							<Grid item xs={12}>
								<Field as={CerebroYesNo} name="buyerProvidingCash" disabled={isSubmitting} />
							</Grid>

							{values.buyerProvidingCash === true ? (
								<>
									<Grid item xs={12} sx={{ marginTop: 3 }}>
										<CerebroLabel required htmlFor="buyerCashAmount">
											What is the maximum amount of cash the buyer is willing to contribute?
										</CerebroLabel>
									</Grid>
									<Grid item xs={12} sm={12} md={6}>
										<Box sx={{ marginBottom: 2 }}>
											<FastField
												formatas="currency"
												as={CerebroMaskedInput}
												maxLength={MaxCurrencyLength}
												id="buyerCashAmount"
												name="buyerCashAmount"
												value={values.buyerCashAmount}
												disabled={isSubmitting}
												error={touched.buyerCashAmount && errors.buyerCashAmount}
											/>
										</Box>
									</Grid>

									<Grid item xs={12} sx={{ marginTop: 3 }}>
										<CerebroLabel htmlFor="acquisitionWorkingCapitalContributionAmount">
											Amount of buyer cash contribution to be used for working capital?
										</CerebroLabel>
									</Grid>
									<Grid item xs={12} sm={12} md={6}>
										<Box sx={{ marginBottom: 2 }}>
											<FastField
												formatas="currency"
												as={CerebroMaskedInput}
												maxLength={MaxCurrencyLength}
												id="acquisitionWorkingCapitalContributionAmount"
												name="acquisitionWorkingCapitalContributionAmount"
												value={values.acquisitionWorkingCapitalContributionAmount}
												disabled={isSubmitting}
												error={
													touched.acquisitionWorkingCapitalContributionAmount &&
													errors.acquisitionWorkingCapitalContributionAmount
												}
											/>
										</Box>
									</Grid>

									<Grid item xs={12} sx={{ marginTop: 3 }}>
										<CerebroLabel htmlFor="acquisitionTransactionCostsAmount">
											Amount of buyer cash contribution to be used for transaction costs?
										</CerebroLabel>
									</Grid>
									<Grid item xs={12} sm={12} md={6}>
										<Box sx={{ marginBottom: 2 }}>
											<FastField
												formatas="currency"
												as={CerebroMaskedInput}
												maxLength={MaxCurrencyLength}
												id="acquisitionTransactionCostsAmount"
												name="acquisitionTransactionCostsAmount"
												value={values.acquisitionTransactionCostsAmount}
												disabled={isSubmitting}
												error={
													touched.acquisitionTransactionCostsAmount &&
													errors.acquisitionTransactionCostsAmount
												}
											/>
										</Box>
									</Grid>
								</>
							) : (
								<></>
							)}
						</Grid>
						{touched.buyerCashAmount && errors.buyerCashAmount && (
							<CerebroText variant="error" sx={{ marginBottom: 2 }}>
								{errors.buyerCashAmount}
							</CerebroText>
						)}

						<Grid container spacing={2}>
							<Grid item xs={12}>
								<CerebroLabel sx={{ marginTop: 3 }} htmlFor="hasLegalCouncel">
									Have you engaged legal counsel?
								</CerebroLabel>
							</Grid>
							<Grid item xs={12}>
								<Box sx={{ marginBottom: 2 }}>
									<Field as={CerebroYesNo} name="hasLegalCouncel" disabled={isSubmitting} />
								</Box>
							</Grid>

							{values.hasLegalCouncel === true ? (
								<>
									<Grid item xs={12}>
										<CerebroLabel required htmlFor="legalFirmName">
											Name of company&#39;s legal firm
										</CerebroLabel>
									</Grid>
									<Grid item xs={12} sm={12} md={6}>
										<Box sx={{ marginBottom: 2 }}>
											<Field
												as={CerebroInput}
												id="legalFirmName"
												name="legalFirmName"
												disabled={isSubmitting}
												value={values.legalFirmName}
												error={touched.legalFirmName && errors.legalFirmName}
											/>
										</Box>
									</Grid>
								</>
							) : (
								<></>
							)}
						</Grid>
						{touched.legalFirmName && errors.legalFirmName && (
							<CerebroText variant="error" sx={{ marginBottom: 2 }}>
								{errors.legalFirmName}
							</CerebroText>
						)}

						<AcquisitionTotalsField name="totalAcquisitionAmount" equityRoll="sellerEquityRoll" />

						<Grid
							container
							sx={{ paddingBottom: 2, marginTop: 6 }}
							justifyContent={{ xs: 'center', md: 'flex-start' }}
						>
							<Grid item>
								<CerebroPrimaryButton loading={isSubmitting} type="submit">
									Save &amp; Continue
								</CerebroPrimaryButton>
							</Grid>
						</Grid>
					</CerebroForm>
				)}
			</Formik>

			<CerebroDrawer open={open} onClose={() => setOpen(false)}>
				<CerebroIcon sx={{ marginBottom: 2 }}>
					<FontAwesomeIcon icon={faInfoCircle} size="3x" />
				</CerebroIcon>

				<CerebroLabel size="large" sx={{ marginBottom: 1 }}>
					Stand-Alone
				</CerebroLabel>
				<CerebroText variant="small" sx={{ marginBottom: 2.5 }}>
					You are purchasing a business and do not currently own one.
				</CerebroText>

				<CerebroLabel size="large" sx={{ marginBottom: 1 }}>
					Add-On
				</CerebroLabel>
				<CerebroText variant="small" sx={{ marginBottom: 2.5 }}>
					You currently own a business or portfolio of companies and are acquiring another. In an add-on
					acquisition, the target company is often adjacent or complementary to the existing business and is
					being acquired for strategic or growth purposes.
				</CerebroText>

				<CerebroLabel size="large" sx={{ marginBottom: 1 }}>
					Partner Buy-Out
				</CerebroLabel>
				<CerebroText variant="small" sx={{ marginBottom: 2.5 }}>
					You own a business with others and are acquiring part or all of your partner or partners’ ownership
					stake(s).
				</CerebroText>

				<CerebroLabel size="large" sx={{ marginBottom: 1 }}>
					Management Buy-Out
				</CerebroLabel>
				<CerebroText variant="small" sx={{ marginBottom: 2.5 }}>
					You are employed by the target business (typically in a management/leadership role) and are
					purchasing part or all of the business from its current owners.
				</CerebroText>
			</CerebroDrawer>
		</CerebroPageContainer>
	);
};

interface DealRequestAllocationAcquisitionValues {
	dealType?: DealType;
	percentageBeingPurchased?: string;
	acquisitionEvaluationStage?: AcquisitionEvaluationStage;
	natureOfBuyer?: NatureOfBuyerType;
	acquisitionYearsInOperation?: string;
	transactionType?: AcquisitionTransactionType;
	hasLegalCouncel?: boolean;
	legalFirmName?: string;
	hasCurrentOwnership?: boolean;
	currentOwnershipPercentage?: string;
	valueOfferedAmount: string;
	sellerEquityRollAmount: string;
	provideSellerFinancing?: boolean;
	sellerFinancingAmount: string;
	buyerProvidingCash?: boolean;
	buyerCashAmount: string;
	acquisitionTransactionCostsAmount: string;
	acquisitionWorkingCapitalContributionAmount: string;
	hasEarnout?: boolean;
	earnoutAmount: string;
	totalAcquisitionAmount: number;
	workingCapitalForAcquisition?: boolean;
	workingCapitalForAcquisitionAmount: string;
}

type DealRequestAllocationAcquisitionValueErrors = Partial<
	Record<keyof DealRequestAllocationAcquisitionValues, string>
>;

function calculateSellerEquityRollAmount(valueOfferedAmount: number, percentagePurchased: number): number {
	return valueOfferedAmount * ((100.0 - percentagePurchased) / 100.0);
}

function runEquityAmountCalculation(
	valueOfferedAmount: number,
	percentagePurchased: number,
	sellerEquityRollAmount: number,
	setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void
) {
	if (percentagePurchased == 100) {
		setFieldValue('sellerEquityRollAmount', NumberUtils.FormatCurrency(0));
	} else {
		if (
			percentagePurchased &&
			percentagePurchased != 100 &&
			sellerEquityRollAmount == 0 &&
			valueOfferedAmount > 0
		) {
			const equityRoll = calculateSellerEquityRollAmount(valueOfferedAmount, percentagePurchased);
			setFieldValue('sellerEquityRollAmount', NumberUtils.FormatCurrency(equityRoll));
		}
	}
}

function calculateAcquisitionTotalAmount(
	valueOfferedAmount: number,
	hasEarnout: boolean,
	earnoutAmount: number,
	sellerEquityRoll: number,
	provideSellerFinancing: boolean,
	sellerFinancingAmount: number,
	buyerProvidingCash: boolean,
	buyerCashAmount: number,
	hasWorkingCapitalLine: boolean,
	workingCapitalLineAmount: number,
	acquisitionTransactionCostsAmount: number,
	acquisitionWorkingCapitalContributionAmount: number
): number {
	const sellerAmount = provideSellerFinancing ? sellerFinancingAmount : 0;
	const totalCashAmount = buyerProvidingCash ? buyerCashAmount : 0;
	const transactionCosts = buyerProvidingCash ? acquisitionTransactionCostsAmount : 0;
	const workingCapitalContribution = buyerProvidingCash ? acquisitionWorkingCapitalContributionAmount : 0;
	const earnout = hasEarnout ? earnoutAmount : 0;
	const lineAmount = hasWorkingCapitalLine ? workingCapitalLineAmount : 0;

	return (
		valueOfferedAmount -
		earnout -
		sellerEquityRoll -
		sellerAmount -
		totalCashAmount +
		transactionCosts +
		workingCapitalContribution -
		lineAmount
	);
}

function validateForm(values: DealRequestAllocationAcquisitionValues) {
	const errors: DealRequestAllocationAcquisitionValueErrors = {};

	if (!values.dealType) {
		errors.dealType = 'Need to select the type of business acquisition you are doing!';
	}

	if (!values.percentageBeingPurchased || NumberUtils.ParseDecimal(values.percentageBeingPurchased, 0) === 0) {
		errors.valueOfferedAmount = 'Percentage of business being purchased is required!';
	}

	if (
		values.dealType === 'AcquisitionManagementBuyout' &&
		values.hasCurrentOwnership === true &&
		(!values.currentOwnershipPercentage || NumberUtils.ParseDecimal(values.currentOwnershipPercentage, 0) === 0)
	) {
		errors.currentOwnershipPercentage = 'Current ownership percentage needs to be provided!';
	}

	if (!values.acquisitionYearsInOperation) {
		errors.acquisitionYearsInOperation = 'Years in operation is required!';
	}

	if (NumberUtils.ParseDecimal(values.valueOfferedAmount, 0) === 0) {
		errors.valueOfferedAmount = 'Value being offered is a required field!';
	}

	if (values.provideSellerFinancing && NumberUtils.ParseDecimal(values.sellerFinancingAmount, 0) === 0) {
		errors.sellerFinancingAmount = 'Seller amount needs to be provided!';
	}

	if (values.buyerProvidingCash && NumberUtils.ParseDecimal(values.buyerCashAmount, 0) === 0) {
		errors.buyerCashAmount = 'Buyer amount needs to be provided!';
	}

	if (values.hasEarnout && NumberUtils.ParseDecimal(values.earnoutAmount, 0) === 0) {
		errors.earnoutAmount = 'Earnout amount needs to be provided!';
	}

	if (values.hasLegalCouncel && !values.legalFirmName) {
		errors.legalFirmName = 'Company legal firm name needs to be provided!';
	}

	return errors;
}
